import React from 'react';
import { setLegacyForced } from './legacy-mode';

/** The app's accent, `$primary` in assets/custom.scss. */
const PRIMARY = 'var(--app-accent)';

/**
 * Which Studio this person gets, for a tenant that has the new one.
 *
 * `forced` is a prop, not read from storage here: the page is server-rendered
 * and then hydrated, storage does not exist on the server, and a render that
 * reads it cannot match the server's markup. The owner resolves it after mount
 * and passes it down.
 *
 * Flipping reloads. The flag helpers memoise per tab because the answer decides
 * page structure, so the honest way to apply a change everywhere is to start
 * the tab again.
 */
export function LegacyExperienceToggle({
  tenantId,
  forced,
}: {
  tenantId: string;
  forced: boolean;
}) {
  const select = (wantLegacy: boolean) => {
    if (wantLegacy === forced) return;
    setLegacyForced(tenantId, wantLegacy);
    window.location.reload();
  };

  const segment = (active: boolean): React.CSSProperties => ({
    border: 'none',
    background: active ? PRIMARY : 'transparent',
    color: active ? '#ffffff' : '#495057',
    fontWeight: active ? 600 : 500,
    fontSize: '13px',
    padding: '7px 14px',
    cursor: active ? 'default' : 'pointer',
    whiteSpace: 'nowrap',
  });

  return (
    <div
      role="group"
      aria-label="Studio experience"
      style={{
        display: 'inline-flex',
        border: '1px solid #d9dce1',
        borderRadius: '6px',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => select(true)}
        aria-pressed={forced}
        style={segment(forced)}
      >
        Classic experience
      </button>
      <button
        type="button"
        onClick={() => select(false)}
        aria-pressed={!forced}
        style={segment(!forced)}
      >
        New experience{' '}
        <span style={{ fontSize: '11px', opacity: 0.85 }}>(beta)</span>
      </button>
    </div>
  );
}
